/**
 * Mobile Cash Advance — request, track and settle advances for field staff.
 * Backend: backend/routers/cash_advances.py (fully implemented, no backend changes needed)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Wallet, Plus, X, Check, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { formatINR } from '../../lib/format';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const APPROVER_ROLES = new Set(['super_admin', 'admin', 'manager', 'project_head', 'project_manager', 'site_engineer', 'finance', 'hr']);

const STATUS_BADGE = {
  Requested: 'bg-amber-100 text-amber-700',
  Approved:  'bg-blue-100 text-blue-700',
  Disbursed: 'bg-indigo-100 text-indigo-700',
  Settled:   'bg-emerald-100 text-emerald-700',
  Rejected:  'bg-rose-100 text-rose-700',
};

export default function MobileCashAdvance() {
  const { user } = useAuth() || {};
  const canApprove = APPROVER_ROLES.has((user?.role || '').toLowerCase());

  const [tab, setTab] = useUrlTab('tab', 'my');
  const [advances, setAdvances] = useState([]);
  const [pending, setPending] = useState([]);
  const [kpi, setKpi] = useState({ open: 0, outstanding: 0, pending_count: 0, settled: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount: '', purpose: '', project_name: '', expected_settlement_date: '' });
  const [saving, setSaving] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const empId = user?.employee_id || user?.linked_employee_uuid || user?.user_id || '';
      const [myRes, pendRes] = await Promise.all([
        apiGet('/cash-advances', { employee_id: empId, limit: 50 }),
        canApprove ? apiGet('/cash-advances', { status: 'Requested', limit: 50 }) : Promise.resolve({ items: [] }),
      ]);
      const myItems = myRes?.items || [];
      setAdvances(myItems);
      setPending(pendRes?.items || []);

      const now = new Date();
      const thirtyAgo = new Date(now - 30 * 86400000).toISOString();
      setKpi({
        open: myItems.filter(a => ['Requested', 'Approved', 'Disbursed'].includes(a.status)).length,
        outstanding: myItems.reduce((s, a) => s + (parseFloat(a.outstanding_amount) || 0), 0),
        pending_count: myItems.filter(a => a.status === 'Requested').length,
        settled: myItems.filter(a => a.status === 'Settled' && a.requested_at >= thirtyAgo).length,
      });
    } catch { toast.error('Failed to load advances'); }
    finally { setLoading(false); }
  }, [user, canApprove]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.amount || isNaN(parseFloat(form.amount))) return toast.error('Enter a valid amount');
    if (!form.purpose.trim() || form.purpose.trim().length < 4) return toast.error('Purpose must be at least 4 characters');
    setSaving(true);
    try {
      await apiPost('/cash-advances', {
        employee_id: user?.employee_id || user?.user_id || '',
        employee_name: user?.name || '',
        amount: parseFloat(form.amount),
        purpose: form.purpose.trim(),
        project_name: form.project_name || undefined,
        expected_settlement_date: form.expected_settlement_date || undefined,
      });
      toast.success('Cash advance requested');
      setShowForm(false);
      setForm({ amount: '', purpose: '', project_name: '', expected_settlement_date: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Request failed'); }
    finally { setSaving(false); }
  };

  const approve = async (id) => {
    try {
      await apiPut(`/cash-advances/${id}/approve`, {});
      toast.success('Approved'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) return toast.error('Reason required');
    try {
      await apiPut(`/cash-advances/${rejectId}/reject`, { reason: rejectReason });
      toast.success('Rejected'); setRejectId(null); setRejectReason(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reject failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-10" data-testid="m-cash-advance-home">
      {/* Header */}
      <div className="bg-[#3b4872] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/m/petty-cash" className="p-1.5 -ml-1.5"><ArrowLeft size={20} /></Link>
        <div>
          <div className="font-display font-bold flex items-center gap-1.5"><Wallet size={14} /> Cash Advance</div>
          <div className="text-[10px] text-slate-300">Request · track · settle</div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-2" data-testid="m-ca-kpis">
          <Kpi label="Open Advances" value={kpi.open} accent="blue" />
          <Kpi label="Total Outstanding" value={formatINR(kpi.outstanding)} accent="amber" />
          <Kpi label="Pending Approval" value={kpi.pending_count} accent="rose" />
          <Kpi label="Settled (30d)" value={kpi.settled} accent="emerald" />
        </div>

        {/* Request button */}
        <button
          onClick={() => setShowForm(true)}
          data-testid="m-ca-request-btn"
          className="w-full bg-[#3b4872] text-white rounded-xl py-3 font-display font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
        >
          <Plus size={16} /> Request Advance
        </button>

        {/* Tab bar (if approver) */}
        {canApprove && (
          <div className="flex gap-2">
            {['my', 'approvals'].map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`flex-1 py-2 rounded-lg text-sm font-display font-bold border transition-all ${tab === t ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-200'}`}
                data-testid={`m-ca-tab-${t}`}>
                {t === 'my' ? 'My Advances' : `Pending (${pending.length})`}
              </button>
            ))}
          </div>
        )}

        {/* My Advances list */}
        {(tab === 'my' || !canApprove) && (
          <div className="space-y-2">
            {loading && <div className="text-center text-slate-400 py-6"><Loader2 size={16} className="animate-spin inline mr-2" />Loading…</div>}
            {!loading && advances.length === 0 && <div className="text-center text-slate-400 italic py-6 text-sm">No advances yet.</div>}
            {advances.map(a => (
              <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`m-ca-row-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-bold text-sm">{a.advance_no}</div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{a.purpose}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_BADGE[a.status] || 'bg-slate-100 text-slate-600'}`}>{a.status}</span>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs">
                  <span className="font-bold text-slate-800">{formatINR(a.amount)}</span>
                  {parseFloat(a.outstanding_amount) > 0 && (
                    <span className="text-amber-700">Outstanding: {formatINR(a.outstanding_amount)}</span>
                  )}
                  {a.project_name && <span className="text-slate-400 truncate">{a.project_name}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Approvals list */}
        {canApprove && tab === 'approvals' && (
          <div className="space-y-2">
            {loading && <div className="text-center text-slate-400 py-6"><Loader2 size={16} className="animate-spin inline mr-2" />Loading…</div>}
            {!loading && pending.length === 0 && <div className="text-center text-slate-400 italic py-6 text-sm">No pending advances.</div>}
            {pending.map(a => (
              <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid={`m-ca-pending-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-bold text-sm">{a.employee_name}</div>
                    <div className="text-xs text-slate-500">{a.advance_no} · {formatINR(a.amount)}</div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{a.purpose}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_BADGE[a.status] || 'bg-slate-100 text-slate-600'}`}>{a.status}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approve(a.id)} data-testid={`m-ca-approve-${a.id}`}
                    className="flex-1 bg-emerald-600 text-white rounded-lg py-2 text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.97]">
                    <Check size={14} /> Approve
                  </button>
                  <button onClick={() => setRejectId(a.id)} data-testid={`m-ca-reject-${a.id}`}
                    className="flex-1 bg-rose-100 text-rose-700 rounded-lg py-2 text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.97]">
                    <X size={14} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Request form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
          <div className="bg-white w-full max-w-lg rounded-t-2xl p-5 space-y-4 max-h-[85vh] overflow-y-auto" data-testid="m-ca-request-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Request Cash Advance</h3>
              <button onClick={() => setShowForm(false)} className="p-1"><X size={18} /></button>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Amount (₹) *</label>
              <input type="number" min="1" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. 5000" data-testid="m-ca-amount-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Purpose *</label>
              <input type="text" value={form.purpose}
                onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. Site materials purchase" data-testid="m-ca-purpose-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Project (optional)</label>
              <input type="text" value={form.project_name}
                onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Project name" data-testid="m-ca-project-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Expected Settlement Date</label>
              <input type="date" value={form.expected_settlement_date}
                onChange={e => setForm(f => ({ ...f, expected_settlement_date: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                data-testid="m-ca-date-input" />
            </div>
            <button onClick={submit} disabled={saving}
              className="w-full bg-[#3b4872] text-white rounded-xl py-3 font-display font-bold disabled:opacity-50"
              data-testid="m-ca-submit-btn">
              {saving ? <Loader2 size={16} className="animate-spin inline mr-2" /> : null}
              Submit Request
            </button>
          </div>
        </div>
      )}

      {/* Reject reason modal */}
      {rejectId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
          <div className="bg-white w-full max-w-lg rounded-t-2xl p-5 space-y-4" data-testid="m-ca-reject-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold">Reject Advance</h3>
              <button onClick={() => { setRejectId(null); setRejectReason(''); }}><X size={18} /></button>
            </div>
            <textarea rows={3} value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Reason for rejection…" data-testid="m-ca-reject-reason" />
            <button onClick={reject}
              className="w-full bg-rose-600 text-white rounded-xl py-3 font-display font-bold"
              data-testid="m-ca-reject-confirm">
              Confirm Rejection
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent = 'slate' }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-display">{label}</div>
      <div className={`text-lg font-bold mt-0.5 text-${accent}-700 truncate`}>{value}</div>
    </div>
  );
}
